import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Pagination,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Add,
  Search,
  MoreVert,
  Visibility,
  Edit,
  Delete,
  ShoppingCart,
  CheckCircle,
  Pending,
  Inventory,
  PlaylistAddCheck,
  TrendingUp,
  ReceiptLong,
  LocalShipping,
  HourglassEmpty,
} from "@mui/icons-material";
import { purchaseAPI } from "../../services/API";
import { useSnackbar } from "notistack";
import { useAuth } from '../../../../INV/pharmecy/contexts/AuthContext';

export const InvPurchase = () => {
  const theme = useTheme();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { hasAnyRole } = useAuth();

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPurchaseOrders();
    }, 500);

    return () => clearTimeout(timer);
  }, [pagination.page, pagination.limit, searchTerm]);

  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
      };

      const response = await purchaseAPI.getAll(params);
      const { purchaseOrders: ordersData, pagination: paginationData } =
        response.data.data;

      setPurchaseOrders(ordersData || []);
      setPagination((prev) => ({
        ...prev,
        total: paginationData.total,
        pages: paginationData.pages,
      }));
    } catch (error) {
      enqueueSnackbar("Error fetching purchase orders", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, page: value }));
  };

  const handleMenuOpen = (event, order) => {
    setAnchorEl(event.currentTarget);
    setSelectedOrder(order);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleView = () => {
    if (selectedOrder) {
      navigate(`/hospital/hospital-inventory/purchases/${selectedOrder._id}`);
      setSelectedOrder(null); 
    }
    setAnchorEl(null);
  };

  const handleEdit = () => {
    if (selectedOrder) {
      navigate(`/hospital/hospital-inventory/purchases/${selectedOrder._id}/edit`);
      setSelectedOrder(null);
    }
    setAnchorEl(null);
  };

  const handleReceive = () => {
    if (selectedOrder) {
      navigate(`/hospital/hospital-inventory/purchases/${selectedOrder._id}/receive`);
      handleMenuClose();
    }
  };

  const handlePartialReceive = () => {
    if (selectedOrder) {
      navigate(`/hospital/hospital-inventory/purchases/${selectedOrder._id}/receive?partial=true`);
      handleMenuClose();
    }
  };

  const handleStatusUpdate = () => {
    if (selectedOrder) {
      setSelectedStatus(selectedOrder.status);
      setStatusDialogOpen(true);
      handleMenuClose();
    }
  };

  const handleDelete = () => {
    if (selectedOrder) {
      setDeleteDialogOpen(true);
      handleMenuClose();
    }
  };

  const updateOrderStatus = async () => {
    if (!selectedOrder || !selectedOrder._id) {
      enqueueSnackbar("No order selected", { variant: "error" });
      return;
    }

    try {
      await purchaseAPI.updateStatus(selectedOrder._id, {
        status: selectedStatus,
      });
      enqueueSnackbar("Order status updated successfully", {
        variant: "success",
      });
      setStatusDialogOpen(false);
      setSelectedOrder(null);
      fetchPurchaseOrders();
    } catch (error) {
      enqueueSnackbar("Error updating order status", { variant: "error" });
    }
  };

  const deleteOrder = async () => {
    if (!selectedOrder || !selectedOrder._id) {
      enqueueSnackbar("No order selected", { variant: "error" });
      return;
    }

    try {
      await purchaseAPI.delete(selectedOrder._id);
      enqueueSnackbar("Purchase order deleted successfully", {
        variant: "success",
      });
      setDeleteDialogOpen(false);
      setSelectedOrder(null);
      fetchPurchaseOrders();
    } catch (error) {
      enqueueSnackbar("Error deleting purchase order", { variant: "error" });
    }
  };

  const handleStatusDialogClose = () => {
    setStatusDialogOpen(false);
    setSelectedOrder(null);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setSelectedOrder(null);
  };

  const canEditOrder = (order) => {
    return order && ["draft", "sent"].includes(order.status);
  };

  const canDeleteOrder = (order) => {
    return order && order.status === "draft";
  };

  const canReceiveOrder = (order) => {
    return (
      order &&
      ["confirmed",  "partially_received"].includes(order.status)
    );
  };

  const canPartiallyReceiveOrder = (order) => {
    return (
      order &&
      ["confirmed",  "partially_received"].includes(order.status)
    );
  };

  const canUpdateStatus = (order) => {
    return (
      order && order.status !== "completed" && order.status !== "cancelled"
    );
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "default",
      sent: "info",
      confirmed: "primary",
      partially_received: "warning",
      completed: "success",
      cancelled: "error",
    };
    return colors[status] || "default";
  };

  const getStatusIcon = (status) => {
    const icons = {
      completed: <CheckCircle />,
      confirmed: <CheckCircle />,
      partially_received: <PlaylistAddCheck />,
      sent: <HourglassEmpty />,
      draft: <ReceiptLong />,
      cancelled: <Delete />,
    };
    return icons[status] || <HourglassEmpty />;
  };

  const getCardGradient = (color) => {
    return `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.05)} 100%)`;
  };

  return (
    <Box  
      sx={{
        minHeight:'100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 5,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography 
            variant="h3" 
            fontWeight="800" 
            sx={{ 
              color: theme.palette.primary.dark,
              fontSize: { xs: '2rem', md: '2.5rem' },
              mb: 0.5
            }}
          >
            Purchase Orders
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: theme.palette.text.secondary,
              fontWeight: 400 
            }}
          >
            Manage and track all purchase orders
          </Typography>
        </Box>
        {hasAnyRole(["admin", "inventory_manager"]) && (
          <Button
            variant="contained"
            size="large"
            startIcon={<Add />}
            onClick={() => navigate("/hospital/hospital-inventory/purchases/new")}
            sx={{
              fontSize: '1.1rem',
              py: 1.5,
              px: 3,
              borderRadius: 2,
              boxShadow: 3,
              '&:hover': {
                boxShadow: 6,
              }
            }}
          >
            New Purchase Order
          </Button>
        )}
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} sm={6} md={3} lg={2.4}>
          <Card 
            sx={{ 
              borderRadius: 3,
              background: getCardGradient(theme.palette.primary.main),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              height: '100%'
            }}
          >
            <CardContent sx={{ textAlign: "center", p: 3 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  background: alpha(theme.palette.primary.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2.5
                }}
              >
                <ShoppingCart sx={{ fontSize: 30, color: theme.palette.primary.main }} />
              </Box>
              <Typography variant="h2" fontWeight="700" sx={{ fontSize: '2.5rem' }}>
                {pagination.total}
              </Typography>
              <Typography variant="h6" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                Total Orders
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={2.4}>
          <Card 
            sx={{ 
              borderRadius: 3,
              background: getCardGradient(theme.palette.success.main),
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              height: '100%'
            }}
          >
            <CardContent sx={{ textAlign: "center", p: 3 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  background: alpha(theme.palette.success.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2.5
                }}
              >
                <CheckCircle sx={{ fontSize: 30, color: theme.palette.success.main }} />
              </Box>
              <Typography variant="h2" fontWeight="700" sx={{ fontSize: '2.5rem', color: theme.palette.success.dark }}>
                {purchaseOrders.filter((o) => o.status === "completed").length}
              </Typography>
              <Typography variant="h6" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                Completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={2.4}>
          <Card 
            sx={{ 
              borderRadius: 3,
              background: getCardGradient(theme.palette.warning.main),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              height: '100%'
            }}
          >
            <CardContent sx={{ textAlign: "center", p: 3 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  background: alpha(theme.palette.warning.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2.5
                }}
              >
                <PlaylistAddCheck sx={{ fontSize: 30, color: theme.palette.warning.main }} />
              </Box>
              <Typography variant="h2" fontWeight="700" sx={{ fontSize: '2.5rem', color: theme.palette.warning.dark }}>
                {
                  purchaseOrders.filter(
                    (o) => o.status === "partially_received"
                  ).length
                }
              </Typography>
              <Typography variant="h6" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                Partial Received
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={2.4}>
          <Card 
            sx={{ 
              borderRadius: 3,
              background: getCardGradient(theme.palette.info.main),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              height: '100%'
            }}
          >
            <CardContent sx={{ textAlign: "center", p: 3 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  background: alpha(theme.palette.info.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2.5
                }}
              >
                <TrendingUp sx={{ fontSize: 30, color: theme.palette.info.main }} />
              </Box>
              <Typography variant="h2" fontWeight="700" sx={{ fontSize: '2.5rem', color: theme.palette.info.dark }}>
                {
                  purchaseOrders.filter((o) =>
                    ["sent", "confirmed"].includes(o.status)
                  ).length
                }
              </Typography>
              <Typography variant="h6" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                In Progress
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={2.4}>
          <Card 
            sx={{ 
              borderRadius: 3,
              background: getCardGradient(theme.palette.grey[500]),
              border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
              height: '100%'
            }}
          >
            <CardContent sx={{ textAlign: "center", p: 3 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  background: alpha(theme.palette.grey[500], 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2.5
                }}
              >
                <ReceiptLong sx={{ fontSize: 30, color: theme.palette.grey[600] }} />
              </Box>
              <Typography variant="h2" fontWeight="700" sx={{ fontSize: '2.5rem', color: theme.palette.grey[700] }}>
                {purchaseOrders.filter((o) => o.status === "draft").length}
              </Typography>
              <Typography variant="h6" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                Draft
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search Card */}
      <Card 
        sx={{ 
          mb: 4, 
          borderRadius: 3,
          boxShadow: 2,
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search by PO number, supplier name, or status..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                size="medium"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1.1rem',
                    height: 56,
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 28, color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography 
                variant="h5" 
                sx={{ 
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  textAlign: { md: 'right' }
                }}
              >
                Total: {pagination.total} orders
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: theme.palette.text.secondary,
                  textAlign: { md: 'right' }
                }}
              >
                Page {pagination.page} of {pagination.pages}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Purchase Orders Table */}
      <Card 
        sx={{ 
          borderRadius: 3,
          boxShadow: 3,
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700,
                color: theme.palette.primary.dark
              }}
            >
              Purchase Orders
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress size={60} />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        fontSize: '1.1rem', 
                        fontWeight: 700, 
                        py: 2.5,
                        backgroundColor: theme.palette.primary.light + '15'
                      }}>
                        PO Number
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: '1.1rem', 
                        fontWeight: 700, 
                        py: 2.5,
                        backgroundColor: theme.palette.primary.light + '15'
                      }}>
                        Supplier
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: '1.1rem', 
                        fontWeight: 700, 
                        py: 2.5,
                        backgroundColor: theme.palette.primary.light + '15'
                      }}>
                        Order Date
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: '1.1rem', 
                        fontWeight: 700, 
                        py: 2.5,
                        backgroundColor: theme.palette.primary.light + '15'
                      }}>
                        Items
                      </TableCell>
                      <TableCell align="right" sx={{ 
                        fontSize: '1.1rem', 
                        fontWeight: 700, 
                        py: 2.5,
                        backgroundColor: theme.palette.primary.light + '15'
                      }}>
                        Total Amount
                      </TableCell>
                      <TableCell align="center" sx={{ 
                        fontSize: '1.1rem', 
                        fontWeight: 700, 
                        py: 2.5,
                        backgroundColor: theme.palette.primary.light + '15'
                      }}>
                        Status
                      </TableCell>
                      <TableCell align="center" sx={{ 
                        fontSize: '1.1rem', 
                        fontWeight: 700, 
                        py: 2.5,
                        backgroundColor: theme.palette.primary.light + '15'
                      }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {purchaseOrders.map((order) => (
                      <TableRow 
                        key={order._id} 
                        hover
                        sx={{ 
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.04)
                          }
                        }}
                      >
                        <TableCell sx={{ py: 2.5 }}>
                          <Typography variant="h6" fontWeight="700" sx={{ fontSize: '1.1rem' }}>
                            {order.poNumber}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ID: {order._id?.substring(0, 8)}...
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2.5 }}>
                          <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                            {order.supplier.name}
                          </Typography>
                          <Typography variant="body1" sx={{ fontSize: '0.95rem' }}>
                            {order.supplier.contactPerson}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {order.supplier.email || 'No email'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2.5 }}>
                          <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                            {new Date(order.orderDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2.5 }}>
                          <Chip
                            label={`${order.items.length} items`}
                            size="medium"
                            variant="outlined"
                            sx={{ 
                              fontSize: '1rem',
                              fontWeight: 600,
                              height: 32
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2.5 }}>
                          <Typography variant="h5" fontWeight="800" sx={{ color: theme.palette.success.dark }}>
                            ₹{order.grandTotal?.toFixed(2)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {order.currency || 'INR'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 2.5 }}>
                          <Chip
                            icon={getStatusIcon(order.status)}
                            label={order.status.replace("_", " ").toUpperCase()}
                            color={getStatusColor(order.status)}
                            size="medium"
                            sx={{ 
                              fontSize: '0.95rem',
                              fontWeight: 700,
                              minWidth: 140,
                              height: 36
                            }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ py: 2.5 }}>
                          <IconButton
                            size="large"
                            onClick={(e) => handleMenuOpen(e, order)}
                            sx={{
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                              }
                            }}
                          >
                            <MoreVert sx={{ fontSize: 28 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {purchaseOrders.length === 0 && (
                <Box sx={{ textAlign: "center", py: 10 }}>
                  <ShoppingCart
                    sx={{ fontSize: 96, color: alpha(theme.palette.text.secondary, 0.3), mb: 3 }}
                  />
                  <Typography variant="h4" color="text.secondary" gutterBottom fontWeight={600}>
                    No purchase orders found
                  </Typography>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}
                  >
                    {searchTerm
                      ? "No orders match your search criteria. Try different keywords."
                      : "Start by creating your first purchase order to manage inventory purchases."}
                  </Typography>
                  {hasAnyRole(["admin", "inventory_manager"]) && (
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<Add />}
                      onClick={() => navigate("/hospital/hospital-inventory/purchases/new")}
                      sx={{
                        fontSize: '1.1rem',
                        py: 1.5,
                        px: 4,
                        borderRadius: 2,
                      }}
                    >
                      Create First PO
                    </Button>
                  )}
                </Box>
              )}

              {/* Pagination */}
              {pagination.pages > 1 && (
                <Box sx={{ 
                  display: "flex", 
                  justifyContent: "center", 
                  py: 4,
                  borderTop: `1px solid ${theme.palette.divider}`
                }}>
                  <Pagination
                    count={pagination.pages}
                    page={pagination.page}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                    sx={{
                      '& .MuiPaginationItem-root': {
                        fontSize: '1.1rem',
                        height: 48,
                        minWidth: 48,
                      }
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 240,
            boxShadow: 6,
            mt: 1,
          }
        }}
      >
        <MenuItem 
          onClick={handleView}
          sx={{ 
            fontSize: '1.1rem',
            py: 1.5,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
            }
          }}
        >
          <Visibility sx={{ mr: 2, fontSize: 24, color: theme.palette.primary.main }} />
          View Details
        </MenuItem>

        {hasAnyRole(["admin", "inventory_manager"]) && (
          <MenuItem
            onClick={handleEdit}
            disabled={!canEditOrder(selectedOrder)}
            sx={{ 
              fontSize: '1.1rem',
              py: 1.5,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
              }
            }}
          >
            <Edit sx={{ mr: 2, fontSize: 24, color: theme.palette.info.main }} />
            Edit Order
          </MenuItem>
        )}

        {hasAnyRole(["admin", "inventory_manager"]) && (
          <MenuItem
            onClick={handleReceive}
            disabled={!canReceiveOrder(selectedOrder)}
            sx={{ 
              fontSize: '1.1rem',
              py: 1.5,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
              }
            }}
          >
            <CheckCircle sx={{ mr: 2, fontSize: 24, color: theme.palette.success.main }} />
            Receive Complete
          </MenuItem>
        )}

        {hasAnyRole(["admin", "inventory_manager"]) && (
          <MenuItem
            onClick={handlePartialReceive}
            disabled={!canPartiallyReceiveOrder(selectedOrder)}
            sx={{ 
              fontSize: '1.1rem',
              py: 1.5,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
              }
            }}
          >
            <PlaylistAddCheck sx={{ mr: 2, fontSize: 24, color: theme.palette.warning.main }} />
            Receive Partial
          </MenuItem>
        )}

        {hasAnyRole(["admin", "inventory_manager"]) && (
          <MenuItem
            onClick={handleStatusUpdate}
            disabled={!canUpdateStatus(selectedOrder)}
            sx={{ 
              fontSize: '1.1rem',
              py: 1.5,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
              }
            }}
          >
            <Inventory sx={{ mr: 2, fontSize: 24, color: theme.palette.secondary.main }} />
            Update Status
          </MenuItem>
        )}

        {hasAnyRole(["admin", "inventory_manager"]) && (
          <MenuItem
            onClick={handleDelete}
            disabled={!canDeleteOrder(selectedOrder)}
            sx={{ 
              fontSize: '1.1rem',
              py: 1.5,
              color: theme.palette.error.main,
              '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.1),
              }
            }}
          >
            <Delete sx={{ mr: 2, fontSize: 24 }} />
            Delete Order
          </MenuItem>
        )}
      </Menu>

      {/* Update Status Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={handleStatusDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: '1.5rem', 
          fontWeight: 700,
          backgroundColor: theme.palette.primary.light + '15',
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          Update Order Status
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3, fontWeight: 600 }}>
            PO: {selectedOrder?.poNumber}
          </Typography>
          <FormControl fullWidth size="medium">
            <InputLabel sx={{ fontSize: '1.1rem' }}>Status</InputLabel>
            <Select
              value={selectedStatus}
              label="Status"
              onChange={(e) => setSelectedStatus(e.target.value)}
              sx={{ fontSize: '1.1rem' }}
            >
              <MenuItem value="draft" sx={{ fontSize: '1.1rem' }}>Draft</MenuItem>
              <MenuItem value="sent" sx={{ fontSize: '1.1rem' }}>Sent</MenuItem>
              <MenuItem value="confirmed" sx={{ fontSize: '1.1rem' }}>Confirmed</MenuItem>
              <MenuItem value="cancelled" sx={{ fontSize: '1.1rem' }}>Cancelled</MenuItem>
            </Select>
          </FormControl>
          {selectedStatus === "completed" && (
            <Alert severity="info" sx={{ mt: 3, fontSize: '1rem' }}>
              Note: Marking as completed will require receiving the goods through the receive process.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button 
            onClick={handleStatusDialogClose}
            sx={{ 
              fontSize: '1.1rem',
              px: 3,
              py: 1
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={updateOrderStatus}
            variant="contained"
            disabled={
              !selectedStatus ||
              !selectedOrder ||
              selectedStatus === selectedOrder?.status
            }
            sx={{ 
              fontSize: '1.1rem',
              px: 4,
              py: 1
            }}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: '1.5rem', 
          fontWeight: 700,
          backgroundColor: alpha(theme.palette.error.main, 0.05),
          borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
          color: theme.palette.error.dark
        }}>
          Delete Purchase Order
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <Alert severity="warning" sx={{ mb: 3, fontSize: '1rem' }}>
            This action cannot be undone. This will permanently delete the purchase order.
          </Alert>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Are you sure you want to delete purchase order{" "}
            <Box component="span" sx={{ color: theme.palette.error.main }}>
              {selectedOrder?.poNumber}
            </Box>
            ?
          </Typography>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            <Box component="span" sx={{ fontWeight: 600 }}>Supplier:</Box> {selectedOrder?.supplier?.name}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            <Box component="span" sx={{ fontWeight: 600 }}>Total Amount:</Box> ₹{selectedOrder?.grandTotal?.toFixed(2)}
          </Typography>
          <Typography variant="body1">
            <Box component="span" sx={{ fontWeight: 600 }}>Order Date:</Box> {selectedOrder?.orderDate ? new Date(selectedOrder.orderDate).toLocaleDateString() : 'N/A'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button 
            onClick={handleDeleteDialogClose}
            sx={{ 
              fontSize: '1.1rem',
              px: 3,
              py: 1
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={deleteOrder}
            variant="contained"
            color="error"
            disabled={!selectedOrder}
            sx={{ 
              fontSize: '1.1rem',
              px: 4,
              py: 1
            }}
          >
            Delete Order
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </Box>
  );
};